import { useRouter } from 'next/router'
import { useRef } from 'react'
import { useStore } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { EventSourceMessage } from '@microsoft/fetch-event-source'
import { differenceInMilliseconds } from 'date-fns'
import { useTranslation } from 'next-i18next'

import confetti from 'canvas-confetti'

import useCurrentUser from '~/hooks/useCurrentUser'
import { useChatAi } from '~/redux/apis/oracle'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import type { RootState } from '~/redux/store'
import {
  selectChatAiThreadId,
  selectChatAiThreadMessages,
  setChatAiErrorMessageTypeActionCreator,
  setChatAiIsLoadingActionCreator,
  setCurrentMessageActionCreator,
  markMessageNotSentActionCreator,
  setHintMessageActionCreator,
  setSetStopMessageCreator,
  setThreadChatMessageActionCreator,
} from '~/redux/slices/chat-ai'
import oracleAnalytics from '~/analytics/oracle/oracleAnalytics'
import { ChatAiErrorType } from '~/chat-scenario/types'

import stylesAi from '~/aioracle/ChatAi.module.css'
import { getOracleLinkType } from '~/aioracle/utils/oracleLinkType'
import { useSetCurriculumAiProgressMutation } from '~/curriculum/apis/curriculum-progress.api'
import { useHasPremiumAccess } from '~/redux/utils/subscription'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'

import {
  consumeAutoTriggerActionCreator,
  consumeComebackActionCreator,
  selectLearningContext,
  selectOracleContextPayload,
} from '~/aioracle/slices/learning-context.slice'
import { OracleContext } from '~/aioracle/types/learning-context.types'
import { OraclePlatform } from '~/aioracle/types/oracle-platform.types'
import { OracleEndingEvent, OracleSafetyStatus, OracleTurnEnding } from '~/aioracle/types/oracle-ending.types'
import {
  pauseOracleActionCreator,
  resumeOracleActionCreator,
  selectIsSafetyGated,
} from '~/aioracle/slices/oracle-access.slice'
import { captureTaskSnapshot } from '~/aioracle/taskSnapshot'
import appConfig from '~/config/app'

interface ReturnType {
  /** Resolves with how the turn ended once it has; null when it was cancelled. */
  onSendMessageClick: (
    message: string,
    title?: string,
    image?: string,
    isCurriculum?: boolean,
    curriculumLevelId?: string,
    isCompleted?: boolean,
    isHint?: boolean,
    activityType?: string,
    ansHistory?: string[],
  ) => Promise<OracleTurnEnding | null>
  /** Stops this hook's in-flight message, if any, and unlocks the input. */
  cancelMessage: () => void
}

interface ChatInput {
  input: string
  chat_history: Array<{ type: string; content: string }>
  name?: string
  user_id?: number
  language?: string
  thread_id: string
  /** The retry key: one per student message, unchanged by the 401 retry. */
  request_id: string
  isCurriculum?: boolean
  image?: string
  freemium?: boolean
  country_code?: string
  grade_level?: number
  /** Not a field of `context`: dashboard turns carry no context but must stay identifiable. */
  platform?: OraclePlatform
  /** Absent entirely when the student is not on a learning surface. */
  context?: OracleContext
}

interface BuildChatInputArgs {
  message: string
  context?: OracleContext
  userId?: number
  nickname?: string
  /** Resolved by the caller: `t('user')` cannot be called from a pure function. */
  userLabel: string
  locale?: string
  threadId: string
  requestId: string
  messages: Array<{ isReceived: boolean; message: string }>
  hasFullAccess?: boolean
  curriculumCountryCode?: string
  gradeLevel?: number | null
  isCurriculum?: boolean
  image?: string
  /** Gates the branch below; it is no longer a wire field. */
  isHint?: boolean
  /** `appConfig.isSchoolPlatform`, passed in so both branches are assertable without mocking the module. */
  isSchoolPlatform?: boolean
}

/** Above the chat window (`z-oracle` = 100100), below the image viewer (100200) and toasts.
 *  The canvas is click-through and removes itself after the burst. */
export const CHAT_CONFETTI_Z = 100150

/** The chat-verdict celebration gate. Pure, so the whole decision table is assertable. */
export const shouldCelebrateChatAnswer = ({
  verdict,
  comebackOwner,
  messages,
}: {
  verdict: string | null
  comebackOwner?: string
  messages: Array<{ isReceived?: boolean }>
}): boolean => verdict === 'correct' && !!comebackOwner && messages.some((message) => message.isReceived)

/** The ending an SSE event announces, or null for any other event. Pure, so the mapping is assertable. */
export const endingFromEvent = ({
  event,
  data,
}: Pick<EventSourceMessage, 'event' | 'data'>): OracleTurnEnding | null => {
  if (event === OracleEndingEvent.Success) return OracleTurnEnding.Success
  if (event === OracleEndingEvent.Interrupted) return OracleTurnEnding.Interrupted
  if (event !== OracleEndingEvent.SafetyStatus) return null
  try {
    if (JSON.parse(data).status === OracleSafetyStatus.Paused) return OracleTurnEnding.Paused
  } catch {
    // Unreadable body: the answer was still withheld.
  }
  // Any other status withholds the answer too: the service issue, never a success.
  return OracleTurnEnding.Unavailable
}

/** Capture only with a live interactive instance and no student-attached image. */
export const shouldCaptureSnapshot = (context?: OracleContext, image?: string): boolean => !!context?.instance && !image

/** Copies the snapshot into `instance.imageData`; returns `context` as-is when there is nothing to attach. */
export const withSnapshot = (context: OracleContext | undefined, snapshot: string | null): OracleContext | undefined =>
  snapshot && context ? { ...context, instance: { ...context.instance, imageData: snapshot } } : context

/** Builds the `input` sent to the Oracle. Pure, so the payload is assertable without a store or render. */
export const buildChatInput = ({
  message,
  context,
  userId,
  nickname,
  userLabel,
  locale,
  threadId,
  requestId,
  messages,
  hasFullAccess,
  curriculumCountryCode,
  gradeLevel,
  isCurriculum,
  image,
  isHint,
  isSchoolPlatform,
}: BuildChatInputArgs): ChatInput => {
  const base: ChatInput = {
    input: message,
    chat_history: [],
    user_id: userId,
    name: 'User',
    language: locale,
    thread_id: threadId,
    request_id: requestId,
    isCurriculum,
    image,
    freemium: hasFullAccess,
    country_code: curriculumCountryCode || undefined,
    grade_level: gradeLevel || undefined,
    // Always sent: an omitted key is indistinguishable from an older client that cannot report it.
    platform: isSchoolPlatform ? OraclePlatform.School : OraclePlatform.Web,
    // Conditional spread: an explicit `undefined` would still create the key. In `base`, so both paths keep it.
    ...(context ? { context } : {}),
  }

  if (isCurriculum || isHint) return base

  return {
    ...base,
    chat_history: messages.map((msg) => ({
      type: msg.isReceived ? 'ai' : 'human',
      content: msg.message,
    })),
    name: nickname ?? userLabel,
  }
}
export default function useOracleMessage(): ReturnType {
  const { t } = useTranslation('chatAi')
  const dispatch = useAppDispatch()
  const user = useCurrentUser()
  const router = useRouter()
  const { locale } = router
  const store = useStore<RootState>()
  // School students go through the safety gate; school teachers and staff use the normal route (product
  // decision). Routing, not a security boundary: the Oracle itself refuses school students on /chat/stream.
  const isSafetyGated = useAppSelector(selectIsSafetyGated)
  const chat = useChatAi({ queryUrl: isSafetyGated ? '/school/chat/stream' : '/chat/stream' })
  const abortRef = useRef<AbortController | null>(null)
  const messages = useAppSelector(selectChatAiThreadMessages)
  const avatarPngUrl = user?.avatarPngUrl || 'avatar-portraits/hom-default-avatar.png'
  const [setCurriculumAiProgress] = useSetCurriculumAiProgressMutation()
  const hasFullAccess = useHasPremiumAccess()
  const curriculumCountryCode = useGetUserChosenCurriculumCountryCode()
  const { data: currentGradeData } = useGetCurrentGradeQuery(undefined, { skip: !user })
  const gradeLevel = currentGradeData?.grade
  const context = useAppSelector(selectOracleContextPayload)
  const { comebackOwner } = useAppSelector(selectLearningContext)
  const onSendMessageClick = async (
    message: string,
    title?: string,
    image?: string,
    isCurriculum = false,
    curriculumLevelId?: string,
    isCompleted?: boolean,
    isHint?: boolean,
    // No longer sent, but kept as positional parameters so `useActivityHint`'s call sites still compile.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    activityType?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ansHistory?: string[],
  ): Promise<OracleTurnEnding | null> => {
    dispatch(setChatAiIsLoadingActionCreator(true))
    dispatch(setSetStopMessageCreator(true))
    const timeAtSendMessageClick = new Date()
    let firstEventFlag = false
    const userMessage = {
      message,
      isReceived: false,
      sender: {
        nickname: user?.nickname ?? t('user'),
        avatarPngUrl,
      },
      // Also the request_id: a fresh one per send, so a resend after a lost reply is a new turn.
      id: uuid(),
      image,
    }

    dispatch(setThreadChatMessageActionCreator({ ...userMessage, message: title ?? message }))

    const snapshot = shouldCaptureSnapshot(context, image) ? await captureTaskSnapshot() : null
    const wireContext = withSnapshot(context, snapshot)

    let response = ''
    let run_id = ''
    // Read on open, celebrated only on `success`: the header arrives before the turn is known to succeed.
    let verdict: string | null = null
    let ending: OracleTurnEnding | null = null
    let isFinished = false
    let sawLegacyEnd = false
    const input = buildChatInput({
      message,
      context: wireContext,
      userId: user?.id,
      nickname: user?.nickname,
      userLabel: t('user'),
      locale,
      // From the store, not the render: callers clear the chat (a new thread) and send in the same tick.
      threadId: selectChatAiThreadId(store.getState()),
      requestId: userMessage.id,
      messages,
      hasFullAccess,
      curriculumCountryCode,
      gradeLevel,
      isCurriculum,
      image,
      isHint,
      isSchoolPlatform: appConfig.isSchoolPlatform,
    })

    // Consumed here, not in a callback: the 401 retry re-enters `onOpen`, and `onClose` never runs on abort.
    if (context?.wasAutoTriggered) dispatch(consumeAutoTriggerActionCreator())

    const celebrate = (): void => {
      const rect = document.querySelector('[data-testid=chat-ai-container]')?.getBoundingClientRect()
      const origin = rect
        ? {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          }
        : { x: 0.5, y: 0.5 }
      const base = { particleCount: 80, spread: 70, origin, disableForReducedMotion: true, zIndex: CHAT_CONFETTI_Z }
      confetti({ ...base, angle: 60 })
      confetti({ ...base, angle: 120 })
      dispatch(consumeComebackActionCreator())
    }

    // Once per turn, from whichever callback ends it. Only `success` is a finished answer.
    const finishTurn = (): void => {
      if (isFinished) return
      isFinished = true
      // A stream that closed without naming its ending did not finish either.
      ending = ending ?? OracleTurnEnding.Interrupted
      const isSuccess = ending === OracleTurnEnding.Success
      dispatch(setSetStopMessageCreator(false))
      if (response) {
        const newMessage = {
          message: response
            .replace(/\\\(|\\\)/g, '$')
            .replace(/\\\[|\\\]/g, '$$$')
            .replace(/\n{2,}/g, '\n'),

          isReceived: true,
          isCutOff: !isSuccess,
          sender: { nickname: 'Oracle', avatarPngUrl: '' },
          id: run_id,
          type: 'ai',
        }
        const match = response.match(/\([^)]*(\/bootcamp|\/encyclopedia|\/curriculum\/v2\/grade-|\/content\/)[^)]*\)/g)
        let isStandaloneContentLink = false

        match?.forEach((matchItem) => {
          const link = matchItem.slice(1, -1)
          const linkType = getOracleLinkType(link)
          if (linkType === 'bootcamp') {
            oracleAnalytics.logBootcampLinkReceived(link)
          } else if (linkType === 'encyclopedia') {
            oracleAnalytics.logEncyclopediaLinkReceived(link)
          } else if (linkType === 'curriculum') {
            oracleAnalytics.logCurriculumLinkReceived(link)
          } else if (linkType === 'standalone') {
            oracleAnalytics.logStandaloneLinkReceived(link)
            isStandaloneContentLink = true
          }
        })

        // Standalone content is progress-isolated from curriculum (R5): a response recommending
        // standalone content must never mark the caller's curriculumLevelId complete.
        if (isSuccess && isCompleted === false && curriculumLevelId && !isStandaloneContentLink) {
          setCurriculumAiProgress({ curriculumLevelId })
        }

        dispatch(setHintMessageActionCreator(isHint ?? false))
        dispatch(setThreadChatMessageActionCreator(newMessage))
        oracleAnalytics.logMessageReceived(response, differenceInMilliseconds(new Date(), timeAtSendMessageClick))
      }
      if (isSuccess && shouldCelebrateChatAnswer({ verdict, comebackOwner, messages })) celebrate()
      if (ending === OracleTurnEnding.Interrupted && !response) {
        // Nothing reached the student, so say so, as the legacy `error` event did before endings existed.
        oracleAnalytics.logNetworkError()
        dispatch(setChatAiErrorMessageTypeActionCreator(ChatAiErrorType.Network))
      }
      if (ending === OracleTurnEnding.Paused) {
        dispatch(markMessageNotSentActionCreator(userMessage.id))
        dispatch(pauseOracleActionCreator())
      }
      // The backend let a turn through, so any pause on this device is out of date.
      if (isSuccess) dispatch(resumeOracleActionCreator())
      if (ending === OracleTurnEnding.Unavailable) {
        dispatch(setChatAiErrorMessageTypeActionCreator(ChatAiErrorType.Unavailable))
      }
      dispatch(setChatAiIsLoadingActionCreator(false))
      dispatch(setCurrentMessageActionCreator(null))
    }

    const controller = new AbortController()
    abortRef.current = controller

    const turn = chat({
      data: {
        input,
      },
      signal: controller.signal,
      onOpen: (httpResponse?: Response) => {
        response = ''
        run_id = ''
        dispatch(setChatAiErrorMessageTypeActionCreator(null))
        verdict = httpResponse?.headers?.get('X-Oracle-Answer-Verdict') ?? null
      },
      onMessage: (msg: EventSourceMessage) => {
        const announced = endingFromEvent(msg)
        if (announced) {
          // The first ending wins; the legacy `end` / `error` that follow it change nothing.
          if (!ending) ending = announced
          return
        }
        if (msg.event === 'metadata') {
          const metaObject = JSON.parse(msg.data)
          if (Object.prototype.hasOwnProperty.call(metaObject, 'run_id')) {
            run_id = metaObject.run_id
          }
        } else if (msg.event === 'data' || msg.event === 'end' || msg.event === '') {
          if (msg.event === 'end') sawLegacyEnd = true
          if (!firstEventFlag) {
            oracleAnalytics.logFirstMessageEvent()
            firstEventFlag = true
          }

          dispatch(setChatAiIsLoadingActionCreator(false))
          if (msg.event === 'data') {
            response += JSON.parse(msg.data)

            const newMessage = {
              message: response
                .replace(/\\\(|\\\)/g, '$$')
                .replace(/\\\[|\\\]/g, '$$$')
                .replace(/\n{2,}/g, '\n'),
              isReceived: true,

              sender: { nickname: 'Oracle' },
              id: run_id,
              type: `${stylesAi['receiver']}`,
            }
            dispatch(setCurrentMessageActionCreator(newMessage))
          }
        } else if (msg.event === 'error' && !ending) {
          dispatch(setChatAiErrorMessageTypeActionCreator(ChatAiErrorType.Network))
          dispatch(setSetStopMessageCreator(false))
        }
      },
      onDone: () => {},
      onClose: () => {
        // An Oracle that predates endings closes a finished turn with a bare `end`. A current one always names
        // its ending first (a safety stop included), so this only keeps a lagging deploy from withholding progress.
        if (!ending && sawLegacyEnd) ending = OracleTurnEnding.Success
        finishTurn()
      },
      onError: (errorType: string) => {
        // An ending already arrived: `interrupted` after partial text is followed by `error` and an aborted
        // connection, which lands here. Keep the partial text instead of showing a network error.
        if (ending) {
          finishTurn()
          return
        }
        dispatch(setSetStopMessageCreator(false))
        dispatch(setChatAiIsLoadingActionCreator(false))
        if (
          errorType === ChatAiErrorType.Rate ||
          errorType === ChatAiErrorType.Network ||
          errorType === ChatAiErrorType.Unauthorized
        ) {
          if (errorType === ChatAiErrorType.Rate) {
            oracleAnalytics.logRateLimitHit(messages.length)
          } else if (errorType === ChatAiErrorType.Network) {
            oracleAnalytics.logNetworkError()
          } else {
            oracleAnalytics.logUnauthorized()
          }
          dispatch(setChatAiErrorMessageTypeActionCreator(errorType))
        }
      },
    })

    oracleAnalytics.logSendMessage()
    await turn
    // An aborted turn resolves `chat()` without finishing: whatever ending arrived first was not shown.
    return isFinished ? ending : null
  }

  const cancelMessage = (): void => {
    if (!abortRef.current) return
    abortRef.current.abort()
    abortRef.current = null
    // An aborted turn fires no callback, so the unlock that `onClose`/`onError` would do happens here.
    dispatch(setSetStopMessageCreator(false))
    dispatch(setChatAiIsLoadingActionCreator(false))
    dispatch(setCurrentMessageActionCreator(null))
  }

  return {
    onSendMessageClick,
    cancelMessage,
  }
}
